import '../lohn/lohn.css';
import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, errorText } from '../../api';
import { fieldId, SelectField, useForm, type FormErrors } from '../../auth/CompanyFields';
import { t } from '../../i18n';
import { useToast } from '../../lib/toast';
import { Loading } from '../belege/Pages';
import { Sheet } from '../belege/Sheet';
import { useLetterList } from '../dokumente/Pages';
import { useCloseSheet } from '../lohn/Pages';
import { Upload } from '../lohn/Questionnaire';
import { SENDERS, type Sender } from './labels';

// „Neuen Brief hochladen“ (?neu=1): Foto oder PDF anhängen. Von wem der Brief ist, erkennt die KI; ist sie aus, wählt
// der Mandant den Absender selbst.
export function UploadSheetHost({ clientId, clientName }: { clientId: string; clientName: string }) {
  const [params] = useSearchParams();
  const close = useCloseSheet('neu');
  return params.get('neu') ? <UploadSheet clientId={clientId} clientName={clientName} onClose={close} /> : null;
}

function UploadSheet({ clientId, clientName, onClose }: { clientId: string; clientName: string; onClose(): void }) {
  const toast = useToast();
  const letters = useLetterList(clientId);
  const auto = letters.data?.kiSender;
  const f = useForm({});
  const [file, setFile] = useState<File>();
  const [fileError, setFileError] = useState<string>();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const sender = (f.values.sender ?? '') as Sender | '';
    const errors: FormErrors = {};
    if (!sender && !auto) errors.sender = t('Bitte auswählen, von wem der Brief ist.');
    if (!file) errors.brief = t('Bitte den Brief als Foto oder PDF anhängen.');
    const first = ['sender', 'brief'].find((k) => errors[k]);
    if (first) {
      f.setErrors({ sender: errors.sender });
      setFileError(errors.brief);
      document.getElementById(fieldId(first))?.focus();
      toast(errors[first]!);
      return;
    }
    setBusy(true);
    try {
      // zuerst die Angaben, dann die Datei (so prüft der Server, bevor er speichert)
      const fd = new FormData();
      fd.append('data', JSON.stringify(auto ? {} : { sender }));
      fd.append('brief', file!, file!.name);
      await api.post(`/api/app/briefe/${clientId}`, fd);
      toast(
        auto
          ? t('Brief hochgeladen · Die App ordnet ihn zu und informiert Ihre Kanzlei')
          : t('Brief abgelegt unter Dokumente › Briefe › {sender} · Ihre Kanzlei ist informiert', { sender: t(sender) }),
      );
      onClose();
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }

  if (auto === undefined) {
    return (
      <Sheet title={t('Neuen Brief hochladen')} onClose={onClose}>
        <Loading error={letters.error} />
      </Sheet>
    );
  }

  return (
    <Sheet title={t('Neuen Brief hochladen')} onClose={onClose}>
      <p className="meta">{clientName}</p>
      <form className="form maform mform" onSubmit={submit} noValidate>
        {!auto && (
          <SelectField
            f={f}
            name="sender"
            label={t('Von wem ist der Brief?')}
            required
            options={SENDERS}
            placeholder={t('Bitte wählen')}
          />
        )}
        <Upload
          k="brief"
          label={t('Brief (Foto oder PDF)')}
          required
          file={file}
          error={fileError}
          onFile={(x) => {
            setFile(x);
            setFileError(undefined);
          }}
        />
        {auto && letters.data?.kiTest && (
          <p className="formerr" role="note">
            {t('Testbetrieb: Die KI läuft gerade nicht nur in der EU. Bitte nur Testbriefe hochladen, keine echten.')}
          </p>
        )}
        {error && (
          <p className="formerr" role="alert">
            {error}
          </p>
        )}
        <div className="row">
          <button className="btn2 dark" type="submit" disabled={busy}>
            {busy ? t('Einen Moment …') : t('Brief hochladen')}
          </button>
          <button className="btn2" type="button" onClick={onClose}>
            {t('Abbrechen')}
          </button>
        </div>
        <div className="src">
          {!auto
            ? t('Ihre Kanzlei sieht den Brief sofort und meldet sich, falls etwas zu tun ist.')
            : letters.data?.kiTest
              ? t(
                  'Von wem der Brief ist, erkennt die App selbst. Ihre Kanzlei sieht ihn gleich danach und meldet sich, falls etwas zu tun ist.',
                )
              : t(
                  'Von wem der Brief ist, erkennt die KI (über Google Cloud, Rechenzentren in der EU). Ihre Kanzlei sieht ihn gleich danach und meldet sich, falls etwas zu tun ist.',
                )}{' '}
          {t('Bei mehreren Seiten am besten ein PDF hochladen. Rechnungen und Kontoauszüge gehören unter Finanzbuchhaltung.')}
        </div>
      </form>
    </Sheet>
  );
}
